import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { Avatar } from "../../components/lawyer/LawyerUi";
import LawyerCard, { LawyerCardSkeleton } from "../../components/user/LawyerCard";
import MarketingBanner from "../../components/user/MarketingBanner";
import { apiErrorMessage, userService } from "../../services/userApi";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning,";
  if (h < 17) return "Good Afternoon,";
  return "Good Evening,";
};

const capBadge = (n) => (n > 99 ? "99+" : n);

const UserDashboard = () => {
  const { profile } = useOutletContext();
  const [lawyers, setLawyers] = useState([]);
  const [myLawyersCount, setMyLawyersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [marketingBanner, setMarketingBanner] = useState(null);
  const closeBanner = useCallback(() => setMarketingBanner(null), []);

  useEffect(() => {
    let active = true;
    userService
      .getDashboard()
      .then((res) => {
        if (!active) return;
        if (res?.data?.status) {
          setLawyers(res.data.data?.lawyers || []);
          setMyLawyersCount(Number(res.data.data?.my_lawyers_count) || 0);
          setMarketingBanner(res.data.data?.marketing_banner || null);
        } else {
          toast.error(res?.data?.message || "Unable to load dashboard.");
        }
      })
      .catch((err) => active && toast.error(apiErrorMessage(err)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const unreadNotifications = Number(profile?.unread_notifications) || 0;

  return (
    <div className="lwp-page lwp-dashboard usp-dashboard">
      <Seo title="Home | Legal Platform" noindex />

      <header className="lwp-topbar">
        <Link to="/user/profile" className="lwp-user">
          <Avatar src={profile?.picture} alt={profile?.name || "Profile"} size={64} className="lwp-avatar" />
          <span className="lwp-user-text">
            <small>{greeting()}</small>
            <strong>{profile?.name || " "}</strong>
          </span>
        </Link>

        <div className="lwp-topbar-actions">
          <Link to="/user/requests/new" className="lwp-bell" aria-label="Create new question">
            <i className="bi bi-plus-lg" />
          </Link>
          <Link to="/user/notifications" className="lwp-bell" aria-label="Notifications">
            <i className="bi bi-bell-fill" />
            {unreadNotifications > 0 && <span className="lwp-count">{capBadge(unreadNotifications)}</span>}
          </Link>
        </div>
      </header>

      <h2 className="lwp-headline">Don&apos;t let legal issues hold you back</h2>

      <section className="lwp-cards">
        <div className="lwp-earning-card">
          <div className="lwp-earning-top">
            <div>
              <p className="usp-video-title">Video Call with Lawyer</p>
              <p className="usp-video-sub">Connect instantly with expert lawyers via secure video call.</p>
            </div>
            <span className="lwp-earning-icon">
              <i className="bi bi-camera-video-fill" />
            </span>
          </div>
          <Link to="/user/video-call" className="lwp-earning-btn">
            Start Video Call
          </Link>
        </div>

        <div className="lwp-stack">
          <Link to="/user/my-lawyers" className="lwp-tile usp-tile">
            <span>
              My Lawyers{loading || !myLawyersCount ? "" : ` (${myLawyersCount})`}
              <small>manage your connected lawyers</small>
            </span>
            <span className="lwp-tile-icon">
              <i className="bi bi-person-badge-fill" />
            </span>
          </Link>
          <Link to="/user/lawyers" className="lwp-tile usp-tile">
            <span>
              Hire Lawyers
              <small>Find and hire the best lawyers for your case</small>
            </span>
            <span className="lwp-tile-icon">
              <i className="bi bi-briefcase-fill" />
            </span>
          </Link>
        </div>
      </section>

      <section className="lwp-activities">
        <div className="lwp-section-head">
          <h3>Experts</h3>
          <Link to="/user/lawyers">See All</Link>
        </div>

        {loading ? (
          <div className="usp-lawyer-grid">
            {[0, 1, 2].map((i) => (
              <LawyerCardSkeleton key={i} />
            ))}
          </div>
        ) : lawyers.length === 0 ? (
          <p className="lwp-empty">No experts available right now.</p>
        ) : (
          <div className="usp-lawyer-grid">
            {lawyers.map((l) => (
              <Link key={l.id} to={`/user/lawyers/${l.id}`} className="usp-lawyer-link">
                <LawyerCard lawyer={l} />
              </Link>
            ))}
          </div>
        )}
      </section>

      <MarketingBanner banner={marketingBanner} onClose={closeBanner} />
    </div>
  );
};

export default UserDashboard;
